import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { motion, AnimatePresence } from 'framer-motion';
import s from './Animate.module.scss';

const Animate = ({ type = '', ...restProps }) => {
    const types = {
        fade: AnimateInOut,
        expand: AnimateUpDown,
        slide: AnimateLeftRight,
    };
    const Component = types[type];
    if (!Component) {
        return null;
    }
    return <Component {...restProps} />;
};

Animate.propTypes = {
    type: PropTypes.string.isRequired,
};

const AnimateInOut = ({
    children = null,
    defaultVisible = false,
    isVisible = false,
    className = '',
    duration = 300,
    delay = 0,
    displayVisible = 'block',
    disableLayoutAnimation = false,
    ...restProps
}) => {
    const classes = classNames(
        s.Root,
        s['Root--InOut'],
        { [s['Root--Visible']]: isVisible },
        { [s['Root--Hidden']]: !isVisible },
        className
    );

    return (
        <motion.div
            layout={!disableLayoutAnimation}
            className={classes}
            transition={{ duration: duration / 1000, delay: delay / 1000 }}
            initial={{
                opacity: defaultVisible ? 1 : 0,
                display: defaultVisible ? displayVisible : 'none',
            }}
            animate={{
                opacity: isVisible ? 1 : 0,
                display: displayVisible,
                transitionEnd: {
                    display: isVisible ? displayVisible : 'none',
                },
            }}
            {...restProps}>
            {children}
        </motion.div>
    );
};

AnimateInOut.propTypes = {
    children: PropTypes.node.isRequired,
    defaultVisible: PropTypes.bool,
    isVisible: PropTypes.bool,
    className: PropTypes.string,
    duration: PropTypes.number,
    delay: PropTypes.number,
    displayVisible: PropTypes.string,
    disableLayoutAnimation: PropTypes.bool,
};

const AnimateUpDown = ({
    children = null,
    defaultVisible = false,
    isVisible = false,
    className = '',
    duration = 300,
    delay = 0,
    displayVisible = 'block',
    ...restProps
}) => {
    const classes = classNames(
        s.Root,
        s['Root--UpDown'],
        { [s['Root--Visible']]: isVisible },
        { [s['Root--Hidden']]: !isVisible },
        className
    );

    return (
        <motion.div
            layout
            className={classes}
            transition={{ duration: duration / 1000, delay: delay / 1000 }}
            initial={{
                height: defaultVisible ? 'auto' : 0,
                display: defaultVisible ? displayVisible : 'none',
            }}
            animate={{
                height: isVisible ? 'auto' : 0,
                display: displayVisible,
                //TODO FIX FOR ACCESSIBILITY
                // transitionEnd: {
                //     display: isVisible ? displayVisible : 'none',
                // },
            }}
            {...restProps}>
            {children}
        </motion.div>
    );
};

AnimateUpDown.propTypes = {
    children: PropTypes.node.isRequired,
    defaultVisible: PropTypes.bool,
    isVisible: PropTypes.bool,
    className: PropTypes.string,
    duration: PropTypes.number,
    delay: PropTypes.number,
    displayVisible: PropTypes.string,
};

const AnimateLeftRight = ({
    children = null,
    defaultVisible = false,
    isVisible = false,
    className = '',
    duration = 300,
    delay = 0,
    displayVisible = 'block',
    ...restProps
}) => {
    const classes = classNames(
        s.Root,
        s['Root--LeftRight'],
        { [s['Root--Visible']]: isVisible },
        { [s['Root--Hidden']]: !isVisible },
        className
    );

    return (
        <motion.div
            layout
            className={classes}
            transition={{ duration: duration / 1000, delay: delay / 1000 }}
            initial={{
                x: defaultVisible ? 0 : '100%',
                display: defaultVisible ? displayVisible : 'none',
            }}
            animate={{
                x: isVisible ? 0 : '100%',
                display: displayVisible,
                transitionEnd: {
                    display: isVisible ? displayVisible : 'none',
                },
            }}
            {...restProps}>
            {children}
        </motion.div>
    );
};

AnimateLeftRight.propTypes = {
    children: PropTypes.node.isRequired,
    defaultVisible: PropTypes.bool,
    isVisible: PropTypes.bool,
    className: PropTypes.string,
    duration: PropTypes.number,
    delay: PropTypes.number,
    displayVisible: PropTypes.string,
};

const CollapsibleMotion = ({
    children,
    isOpen = false,
    className = '',
    duration = 300,
    ...restProps
}) => {
    return (
        <AnimatePresence initial={false}>
            {isOpen && (
                <motion.div
                    className={classNames(className)}
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: duration / 1000 }}
                    style={{ overflow: 'hidden' }}
                    {...restProps}>
                    {children}
                </motion.div>
            )}
        </AnimatePresence>
    );
};

CollapsibleMotion.propTypes = {
    children: PropTypes.node.isRequired,
    isOpen: PropTypes.bool,
    className: PropTypes.string,
    duration: PropTypes.number,
};

export default Animate;

export { AnimateInOut, AnimateUpDown, AnimateLeftRight, CollapsibleMotion };
